import { Component, computed, input } from '@angular/core';
import {
  DayOfWeek,
  WorkScheduleDayModel,
} from '../../../../../../shared/store/attendance/work-schedule/types';
import {
  WEEK_DAYS,
  dayAmplitude,
  dayOfWeekLabelKey,
  formatDuration,
  toMinutes,
} from '../work-schedule-helper';

type WorkScheduleWeekRow = {
  dayOfWeek: DayOfWeek;
  labelKey: string;
  /** Null = day not worked. */
  day: WorkScheduleDayModel | null;
  startLabel: string;
  endLabel: string;
  amplitudeMinutes: number;
  amplitudeLabel: string;
  offsetPercent: number;
  widthPercent: number;
};

type WorkScheduleWeekTick = {
  minutes: number;
  label: string;
  offsetPercent: number;
};

/** Smallest axis, so a short or empty plan never stretches its bars over the whole track. */
const MIN_AXIS_START = 7 * 60;
const MIN_AXIS_END = 19 * 60;

@Component({
  selector: 'app-work-schedule-week',
  standalone: false,
  templateUrl: './work-schedule-week.component.html',
  styleUrl: './work-schedule-week.component.scss',
})
export class WorkScheduleWeekComponent {
  /** Worked days, in any order. A day absent from the list is not worked. */
  readonly days = input<WorkScheduleDayModel[]>([]);

  /** Common time axis of the seven rows, rounded to the hour. */
  private readonly axis = computed(() => {
    const validDays = this.days().filter((x) => dayAmplitude(x) > 0);
    const start = Math.min(
      MIN_AXIS_START,
      ...validDays.map((x) => Math.floor(toMinutes(x.startTime) / 60) * 60),
    );
    const end = Math.max(
      MIN_AXIS_END,
      ...validDays.map((x) => Math.ceil(toMinutes(x.endTime) / 60) * 60),
    );
    return { start, span: end - start };
  });

  readonly ticks = computed<WorkScheduleWeekTick[]>(() => {
    const { start, span } = this.axis();
    const step = span > 12 * 60 ? 120 : 60;
    const ticks: WorkScheduleWeekTick[] = [];
    for (let minutes = start; minutes <= start + span; minutes += step) {
      ticks.push({
        minutes,
        label: `${(minutes / 60).toString().padStart(2, '0')}:00`,
        offsetPercent: ((minutes - start) / span) * 100,
      });
    }
    return ticks;
  });

  readonly rows = computed<WorkScheduleWeekRow[]>(() => {
    const { start, span } = this.axis();
    return WEEK_DAYS.map((dayOfWeek) => {
      const day = this.days().find((x) => x.dayOfWeek === dayOfWeek) ?? null;
      const amplitudeMinutes = day == null ? 0 : dayAmplitude(day);
      return {
        dayOfWeek,
        labelKey: dayOfWeekLabelKey(dayOfWeek),
        day,
        startLabel: day?.startTime?.substring(0, 5) ?? '',
        endLabel: day?.endTime?.substring(0, 5) ?? '',
        amplitudeMinutes,
        amplitudeLabel:
          amplitudeMinutes > 0 ? formatDuration(amplitudeMinutes) : '-',
        offsetPercent:
          amplitudeMinutes > 0
            ? ((toMinutes(day!.startTime) - start) / span) * 100
            : 0,
        widthPercent: (amplitudeMinutes / span) * 100,
      };
    });
  });

  readonly workedDayCount = computed(() => this.days().length);

  readonly weekAmplitudeLabel = computed(() =>
    formatDuration(
      this.days().reduce((total, x) => total + dayAmplitude(x), 0),
    ),
  );
}
